import React, { useEffect, useState } from 'react'
import { Input, Select, Spinner, Switcher, Tooltip, Upload } from 'components/ui'
import { FieldLabel } from 'components/shared'
import { useTranslation } from 'react-i18next'
import { formatterInputSelectData } from 'utils/storeHube/helpers'
import { apiGetVisualCheckTemplates } from 'services/VisualCheckTemplateServices'
import { handleNameChange, onCheckRequired } from './helpers'
import { FiSave } from 'react-icons/fi'
import { HiOutlineTrash } from 'react-icons/hi'
import { FcImageFile } from 'react-icons/fc'
import AnswerField from './AnswerField'

// Igual patron que QuestionTypeFilePicker/QuestionTypeRadio: un solo
// componente que sirve tanto al builder (crear/editar la pregunta, con
// question_option ya persistidas) como a la pantalla de respuesta del
// auditor (isAnswer). Las 2 opciones Sí/No son fijas — acá solo se muestran
// (sin +/- ni edición de nombre); la config de "generar ticket automático"
// por opción se hace aparte, en el flujo existente de AddTiccketAutomatic
// (reusado sin cambios vía AddTicketAutomaticTypeVisualValidation).
const QuestionTypeVisualValidation = ({ handleAddNewQuestion, questionTypeId, question, edit, handleRemoveQuestion, isAnswer, questionForAnswer, onChange, errorMessage }) => {

    const { t } = useTranslation()

    const [nameQuestion, setNameQuestion] = useState(question ? question.name : "")
    const [uniqueUuid] = useState(question ? (question.uuid || question.uuid2) : "")
    const [optionListModified] = useState(question ? question.question_option : [])
    const [questionType_id] = useState(question ? question.question_type_id : questionTypeId)
    const [questionRequired, setQuestionRequired] = useState(question ? question.required : false)
    const [templateId, setTemplateId] = useState(question ? question.visual_check_template_id : null)
    const [instructions, setInstructions] = useState(question ? (question.visual_check_instructions || '') : '')
    const [templates, setTemplates] = useState([])
    const [loadingTemplates, setLoadingTemplates] = useState(!isAnswer)
    const [loading, setLoading] = useState(false)
    const [isRemoving, setIsRemoving] = useState(false)
    const selectedTemplate = templates.find(o => o.value === templateId)

    useEffect(() => {
        if (isAnswer) return
        apiGetVisualCheckTemplates({ all: true }).then(({ data }) => {
            setTemplates(formatterInputSelectData(data.data))
            setLoadingTemplates(false)
        }).catch(() => setLoadingTemplates(false))
    }, [isAnswer])

    const updateQuestions = () => {
        setLoading(true)
        handleAddNewQuestion(nameQuestion, optionListModified, questionType_id, questionRequired, uniqueUuid, undefined, undefined, question?.points ?? 0, templateId, instructions)
        setTimeout(() => setLoading(false), 1000)
    }

    useEffect(() => {
        if (!isAnswer && templateId !== null) updateQuestions()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateId, questionRequired, nameQuestion, instructions])

    if (isAnswer) {
        const template = questionForAnswer?.visual_check_template
        return (
            <AnswerField
                label={questionForAnswer?.name}
                required={questionForAnswer?.required}
                error={errorMessage}
            >
                {template && (
                    <div className="flex flex-col gap-2 mb-3">
                        <div className="p-2.5 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-indigo-500 dark:text-indigo-300 mb-0.5">
                                {t('visual_check_answer_howto_label')}
                            </p>
                            <p className="text-xs text-gray-700 dark:text-gray-200">
                                {questionForAnswer.visual_check_instructions || t('visual_check_answer_hint', { template: template.name })}
                            </p>
                        </div>
                        {template.description && (
                            <div className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-600">
                                <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-0.5">
                                    {t('visual_check_answer_about_label')}
                                </p>
                                <p className="text-xs text-gray-600 dark:text-gray-300">{template.description}</p>
                            </div>
                        )}
                    </div>
                )}
                <Upload
                    className="w-full"
                    draggable
                    uploadLimit={1}
                    removeFileState={(event) => onChange(event, 'remove_file')}
                    onChange={(event) => onChange(event, 'change_files')}
                >
                    <div className="my-3 text-center">
                        <div className="text-5xl mb-4 flex justify-center">
                            <FcImageFile />
                        </div>
                        <p className="font-semibold flex flex-col">
                            <span className="text-gray-800 dark:text-white">{t('dragFile')}</span>
                        </p>
                    </div>
                </Upload>
            </AnswerField>
        )
    }

    return (
        <>
            <Input onChange={(event) => handleNameChange(setNameQuestion, event)} placeholder={t("question")} value={nameQuestion} />

            <div className='mb-3 mt-3'>
                <p className='text-sm mb-1'>{t('visual_check_template_select_label')}</p>
                <Select
                    isLoading={loadingTemplates}
                    options={templates}
                    value={selectedTemplate ?? (templateId ? { value: templateId, label: `#${templateId}` } : null)}
                    onChange={opt => setTemplateId(opt?.value ?? null)}
                    placeholder={t('select_placeholder')}
                />
                {selectedTemplate?.description && (
                    <p className='text-xs text-gray-500 mt-1.5'>{selectedTemplate.description}</p>
                )}
            </div>

            <div className='mb-3'>
                <FieldLabel label={t('visual_check_instructions_label')} />
                <Input
                    textArea
                    rows={2}
                    placeholder={t('visual_check_instructions_placeholder')}
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                />
                <p className='text-xs text-gray-400 mt-1'>{t('visual_check_instructions_hint')}</p>
            </div>

            <div className="flex flex-col gap-1.5 mb-3">
                {optionListModified.map((option) => (
                    <div key={option.uuid || option.uuid2} className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                        {option.name}
                    </div>
                ))}
            </div>

            <div className='flex items-center mb-3 justify-end'>
                <div className='flex items-center mr-2'>
                    <span className='mr-2'>{t("isRequired")}</span>
                    <Switcher defaultChecked={question && question.required} onChange={(event) => onCheckRequired(setQuestionRequired, event)} />
                </div>
                {edit && (
                    <>
                        <div
                            className={`mr-5 cursor-pointer transition-colors duration-200 ${isRemoving ? 'text-gray-400 pointer-events-none' : 'hover:text-red-500'}`}
                            onClick={() => {
                                setIsRemoving(true)
                                setTimeout(() => handleRemoveQuestion(question), 600)
                            }}
                        >
                            <Tooltip title={isRemoving ? "Desactivando pregunta..." : "Desactivar pregunta"}>
                                {isRemoving ? <Spinner size={20} /> : <HiOutlineTrash className='text-lg' />}
                            </Tooltip>
                        </div>
                        <div className='mr-5' onClick={() => updateQuestions()}>
                            <Tooltip title="Guardar cambios">
                                {loading ? <Spinner /> : <FiSave className='text-lg' />}
                            </Tooltip>
                        </div>
                    </>
                )}
            </div>
        </>
    )
}

export default QuestionTypeVisualValidation
